import type { ChartConfig } from '../protocol/types'
import type { ChartKind, ChartSpec, ColumnDef, Row } from '../protocol/v2/cards'

export type ChartValue = string | number | boolean

export interface ChartTableProjection {
  columns: ColumnDef[]
  rows: Row[]
  total: number
}

export interface CartesianSeries {
  name: string
  values: Array<number | null>
  /** Dual-axis only: which y-scale this series is drawn against. */
  axis?: 0 | 1
}

export interface ScatterSeries {
  name: string
  points: Array<[number, number]>
}

export type ClosedChartData =
  | {
      kind: 'radar'
      datasetRef: string
      indicators: Array<{ name: string; max: number }>
      series: Array<{ name: string; values: number[] }>
    }
  | {
      kind: 'cartesian'
      datasetRef: string
      categories: ChartValue[]
      series: CartesianSeries[]
      mode: 'standard' | 'grouped' | 'stacked'
      area: boolean
      xName: string
      yNames: string[]
      /** Present only for a dual-axis chart: two y-scales on one plot. */
      axes?: 'dual'
      /** Dual only, left then right. Always non-empty — the renderer never
       * falls back to a raw column id, because an unlabelled scale is exactly
       * what makes a dual-axis chart misread. */
      axisNames?: [string, string]
    }
  | {
      kind: 'scatter'
      datasetRef: string
      series: ScatterSeries[]
      xName: string
      yName: string
    }
  | {
      kind: 'pie'
      datasetRef: string
      items: Array<{ name: ChartValue; value: number }>
      nameField: string
      valueField: string
    }

export interface ChartArtifact {
  kind: 'chart'
  datasetRef: string
  chartKind: Exclude<ChartKind, ''>
  title: string
  summary: string
  data: ClosedChartData
  table: ChartTableProjection
}

export interface ChartProjectionSuccess {
  ok: true
  artifact: ChartArtifact
  config: ChartConfig
}

export interface ChartProjectionFailure {
  ok: false
  reason: string
  table: ChartTableProjection
}

export type ChartProjection = ChartProjectionSuccess | ChartProjectionFailure

const CHART_TYPE: Record<Exclude<ChartKind, ''>, ChartConfig['type']> = {
  bar: 'bar',
  'grouped-bar': 'bar',
  'stacked-bar': 'bar',
  // Dual-axis draws bars, so it shares the bar renderer and adds the second
  // scale through the closed data (`axes: 'dual'`) rather than through a new
  // v1 config type that every existing chart payload would have to tolerate.
  'dual-axis': 'bar',
  line: 'line',
  area: 'line',
  pie: 'pie',
  scatter: 'scatter',
  radar: 'radar',
}

/**
 * Validate and project the neutral protocol spec without accepting renderer
 * options, formatter functions, or HTML. Every plotted value is read directly
 * from the same immutable row projection used by the fallback table.
 */
export function projectChartSpec(input: unknown): ChartProjection {
  const fallback = fallbackTable(input)
  if (!isRecord(input)) return failure('图表规格不是对象。', fallback)
  const chartKind = input.chartKind
  if (!isChartKind(chartKind) || chartKind === '') {
    return failure('没有适合当前数据的图表类型。', fallback)
  }
  if (!isRecord(input.encoding)) return failure('图表缺少字段映射。', fallback)
  if (!Array.isArray(input.data) || input.data.length === 0) {
    return failure('图表没有可绘制的数据行。', fallback)
  }

  const x = input.encoding.x
  const rawY = input.encoding.y
  const seriesKey = input.encoding.series
  const columnLabels = stringMap(input.columnLabels)
  if (typeof x !== 'string' || !x.trim()) return failure('图表缺少维度字段。', fallback)
  if (!Array.isArray(rawY) || rawY.length === 0 || rawY.some(key => typeof key !== 'string' || !key.trim())) {
    return failure('图表至少需要一个度量字段。', fallback)
  }
  const y = rawY as string[]
  if (new Set(y).size !== y.length) return failure('图表度量字段不能重复。', fallback)
  if (y.includes(x)) return failure('维度字段不能同时作为度量字段。', fallback)
  if (seriesKey !== undefined && (typeof seriesKey !== 'string' || !seriesKey.trim())) {
    return failure('系列字段无效。', fallback)
  }
  if (typeof seriesKey === 'string' && (seriesKey === x || y.includes(seriesKey))) {
    return failure('系列字段必须独立于维度和度量字段。', fallback)
  }
  if (chartKind === 'pie' && (y.length !== 1 || seriesKey !== undefined)) {
    return failure('饼图仅支持一个度量且不支持额外系列字段。', fallback)
  }
  let dual: { lineMeasure: string; left: string; right: string } | undefined
  if (chartKind === 'dual-axis') {
    const line = Array.isArray(input.encoding.lineColumns) ? input.encoding.lineColumns : []
    const units = isRecord(input.encoding.axisUnits) ? input.encoding.axisUnits : undefined
    const left = typeof units?.left === 'string' ? units.left.trim() : ''
    const right = typeof units?.right === 'string' ? units.right.trim() : ''
    const measure = line.length === 1 && typeof line[0] === 'string' ? line[0].trim() : ''
    // Both scales must be named and distinct. An unlabelled second axis is what
    // lets a reader attribute a value to the wrong measure, which is the whole
    // hazard of putting two scales on one plot.
    if (!measure || y.includes(measure) || measure === x || seriesKey !== undefined
      || y.length > 2 || !left || !right || left === right) {
      return failure('双轴图需要 1–2 个主轴度量、恰好一个次轴度量，以及两条不同的轴标签；次轴度量不能同时作为主轴度量或系列字段。', fallback)
    }
    dual = { lineMeasure: measure, left, right }
  } else if (input.encoding.lineColumns !== undefined || input.encoding.axisUnits !== undefined) {
    return failure('次轴度量与轴标签仅用于双轴图。', fallback)
  }

  const rows = normalizeRows(input.data)
  if (!rows) return failure('图表数据行或单元格类型无效。', fallback)
  const table = tableFrom(
    rows, x, y, typeof seriesKey === 'string' ? seriesKey : undefined, columnLabels,
  )
  const datasetRef = typeof input.datasetRef === 'string' && input.datasetRef.trim()
    ? input.datasetRef.trim()
    : 'legacy:inline'
  const title = typeof input.title === 'string' ? input.title : ''
  // The secondary axis of a dual-axis chart is a measure in every respect
  // except which scale it is drawn against, so it joins the measure list once.
  const measures = dual === undefined ? y : [...y, dual.lineMeasure]

  for (const row of rows) {
    if (!isDimension(row[x])) return failure(`维度字段 ${x} 含有空值或复杂值。`, table)
    if (typeof seriesKey === 'string' && !isDimension(row[seriesKey])) {
      return failure(`系列字段 ${seriesKey} 含有空值或复杂值。`, table)
    }
    for (const measure of measures) {
      if (toFiniteNumber(row[measure]) === null) {
        return failure(`度量字段 ${measure} 必须逐值为有限数值。`, table)
      }
    }
    if (chartKind === 'scatter' && toFiniteNumber(row[x]) === null) {
      return failure(`散点图维度字段 ${x} 必须逐值为有限数值。`, table)
    }
  }

  // The title is rendered as the card header, so the caption below it starts at
  // the description — repeating the title there reads as a stutter.
  const summary = `按 ${labelOf(x, columnLabels)} 展示 ${measures.map(key => labelOf(key, columnLabels)).join('、')}，共 ${rows.length} 条记录。`
    + (chartKind === 'radar' ? `共同刻度 0–${input.encoding.radarMax}，按原值绘制。` : '')
  let data: ClosedChartData
  if (chartKind === 'radar') {
    const max = input.encoding.radarMax
    if (typeof max !== 'number' || !Number.isFinite(max) || max <= 0
      || y.length < 3 || y.length > 8 || rows.length > 5 || seriesKey !== undefined
      || new Set(rows.map(row => String(row[x]))).size !== rows.length
      || rows.some(row => y.some(key => toFiniteNumber(row[key])! < 0 || toFiniteNumber(row[key])! > max))) {
      return failure('雷达图需要 3–8 个可比指标、最多 5 个不重复对象，以及覆盖全部非负值的明确共同刻度；不自动归一化或填补缺失值。', table)
    }
    data = {
      kind: 'radar', datasetRef,
      indicators: y.map(key => ({ name: labelOf(key, columnLabels), max })),
      series: rows.map(row => ({ name: String(row[x]), values: y.map(key => toFiniteNumber(row[key])!) })),
    }
  } else if (chartKind === 'scatter') {
    data = scatterData(
      rows, x, y, typeof seriesKey === 'string' ? seriesKey : undefined,
      datasetRef, columnLabels,
    )
  } else if (chartKind === 'pie') {
    const values = rows.map(row => toFiniteNumber(row[y[0]!])!)
    if (values.some(value => value < 0) || !values.some(value => value > 0)
      || new Set(rows.map(row => String(row[x]))).size !== rows.length) {
      return failure('饼图需要不重复的类别、非负值及大于零的合计。', table)
    }
    data = {
      kind: 'pie',
      datasetRef,
      nameField: labelOf(x, columnLabels),
      valueField: labelOf(y[0]!, columnLabels),
      items: rows.map(row => ({ name: row[x] as ChartValue, value: toFiniteNumber(row[y[0]!])! })),
    }
  } else {
    const cartesian = cartesianData(
      rows,
      x,
      measures,
      typeof seriesKey === 'string' ? seriesKey : undefined,
      datasetRef,
      chartKind,
      columnLabels,
      dual,
    )
    if (typeof cartesian === 'string') return failure(cartesian, table)
    data = cartesian
  }

  const artifact: ChartArtifact = {
    kind: 'chart',
    datasetRef,
    chartKind,
    title,
    summary,
    data,
    table,
  }
  return {
    ok: true,
    artifact,
    config: { type: CHART_TYPE[chartKind], title, data: { ...data, summary, table } },
  }
}

/** Compatibility helper for callers that only consume the legacy config. */
export function chartSpecToConfig(spec: ChartSpec): ChartConfig | null {
  const projection = projectChartSpec(spec)
  return projection.ok ? projection.config : null
}

function cartesianData(
  rows: Row[],
  x: string,
  measures: string[],
  seriesKey: string | undefined,
  datasetRef: string,
  chartKind: Exclude<ChartKind, '' | 'pie' | 'scatter' | 'radar'>,
  columnLabels: Readonly<Record<string, string>>,
  dual?: { readonly lineMeasure: string; readonly left: string; readonly right: string },
): Extract<ClosedChartData, { kind: 'cartesian' }> | string {
  const categories = uniqueValues(rows.map(row => row[x] as ChartValue))
  const groups = seriesKey ? uniqueValues(rows.map(row => row[seriesKey] as ChartValue)) : []
  const cells = new Map<string, number>()
  for (const row of rows) {
    const category = valueKey(row[x] as ChartValue)
    const group = seriesKey ? valueKey(row[seriesKey] as ChartValue) : ''
    for (const measure of measures) {
      const key = `${category}\u0000${group}\u0000${measure}`
      if (cells.has(key)) return `维度 ${x}${seriesKey ? ` 与系列 ${seriesKey}` : ''} 存在重复点。`
      cells.set(key, toFiniteNumber(row[measure])!)
    }
  }

  const series: CartesianSeries[] = []
  const groupValues = seriesKey ? groups : [null]
  for (const group of groupValues) {
    for (const measure of measures) {
      series.push({
        // A lone measure contributes nothing to a grouped legend entry — the
        // axis summary already names it. Multi-measure charts keep the full
        // "group · measure" pair so entries stay unambiguous.
        name: group === null
          ? labelOf(measure, columnLabels)
          : measures.length === 1
            ? String(group)
            : `${String(group)} · ${labelOf(measure, columnLabels)}`,
        ...(dual === undefined ? {} : { axis: measure === dual.lineMeasure ? 1 as const : 0 as const }),
        values: categories.map(category => cells.get(
          `${valueKey(category)}\u0000${group === null ? '' : valueKey(group)}\u0000${measure}`,
        ) ?? null),
      })
    }
  }
  return {
    kind: 'cartesian',
    datasetRef,
    categories,
    series,
    mode: chartKind === 'stacked-bar' ? 'stacked' : chartKind === 'grouped-bar' ? 'grouped' : 'standard',
    area: chartKind === 'area',
    xName: labelOf(x, columnLabels),
    yNames: measures.map(measure => labelOf(measure, columnLabels)),
    ...(dual === undefined
      ? {}
      : { axes: 'dual' as const, axisNames: [dual.left, dual.right] as [string, string] }),
  }
}

function scatterData(
  rows: Row[],
  x: string,
  measures: string[],
  seriesKey: string | undefined,
  datasetRef: string,
  columnLabels: Readonly<Record<string, string>>,
): Extract<ClosedChartData, { kind: 'scatter' }> {
  const groups = seriesKey ? uniqueValues(rows.map(row => row[seriesKey] as ChartValue)) : [null]
  const series: ScatterSeries[] = []
  for (const group of groups) {
    const groupRows = group === null
      ? rows
      : rows.filter(row => valueKey(row[seriesKey!] as ChartValue) === valueKey(group))
    for (const measure of measures) {
      series.push({
        // Same lone-measure rule as cartesian series naming above.
        name: group === null
          ? labelOf(measure, columnLabels)
          : measures.length === 1
            ? String(group)
            : `${String(group)} · ${labelOf(measure, columnLabels)}`,
        points: groupRows.map(row => [toFiniteNumber(row[x])!, toFiniteNumber(row[measure])!]),
      })
    }
  }
  return {
    kind: 'scatter', datasetRef, series,
    xName: labelOf(x, columnLabels),
    yName: measures.map(measure => labelOf(measure, columnLabels)).join(' / '),
  }
}

function fallbackTable(input: unknown): ChartTableProjection {
  if (!isRecord(input) || !Array.isArray(input.data)) return { columns: [], rows: [], total: 0 }
  const rows = normalizeRows(input.data) ?? []
  const keys = uniqueValues(rows.flatMap(row => Object.keys(row)))
  const labels = stringMap(input.columnLabels)
  return {
    columns: keys.map(key => ({ key, title: labelOf(key, labels), displayName: labelOf(key, labels) })),
    rows,
    total: rows.length,
  }
}

function tableFrom(
  rows: Row[], x: string, y: string[], series: string | undefined,
  labels: Readonly<Record<string, string>>,
): ChartTableProjection {
  const keys = uniqueValues([x, ...(series ? [series] : []), ...y])
  return {
    columns: keys.map(key => ({
      key,
      title: labelOf(key, labels),
      displayName: labelOf(key, labels),
      role: key === x || key === series ? 'dimension' : 'metric',
    })),
    rows: rows.map(row => Object.fromEntries(keys.map(key => [key, row[key] ?? null])) as Row),
    total: rows.length,
  }
}

function stringMap(value: unknown): Readonly<Record<string, string>> {
  if (!isRecord(value)) return {}
  return Object.fromEntries(Object.entries(value)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0))
}

function labelOf(key: string, labels: Readonly<Record<string, string>>): string {
  return labels[key]?.trim() || key
}

function normalizeRows(values: unknown[]): Row[] | null {
  const rows: Row[] = []
  for (const value of values) {
    if (!isRecord(value)) return null
    const row: Row = {}
    for (const [key, cell] of Object.entries(value)) {
      if (cell !== null && typeof cell !== 'string' && typeof cell !== 'number' && typeof cell !== 'boolean') {
        return null
      }
      row[key] = cell
    }
    rows.push(row)
  }
  return rows
}

function isChartKind(value: unknown): value is ChartKind {
  return value === '' || value === 'bar' || value === 'line' || value === 'pie'
    || value === 'scatter' || value === 'area' || value === 'grouped-bar' || value === 'stacked-bar'
    || value === 'radar' || value === 'dual-axis'
}

function isDimension(value: unknown): value is ChartValue {
  return typeof value === 'string' || typeof value === 'boolean' || isFiniteNumber(value)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** JDBC decimal/count values can be serialized as strings to retain precision. */
function toFiniteNumber(value: unknown): number | null {
  if (isFiniteNumber(value)) return value
  if (typeof value !== 'string' || value.trim() === '') return null
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim())) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function uniqueValues<T>(values: T[]): T[] {
  return [...new Map(values.map(value => [valueKey(value), value])).values()]
}

function valueKey(value: unknown): string {
  return `${typeof value}:${String(value)}`
}

function failure(reason: string, table: ChartTableProjection): ChartProjectionFailure {
  return { ok: false, reason, table }
}
